import React from "react";
import { BmModal } from "../../../ForBrands/ui/BrandUI";
import { ArchetypeDot } from "../../../ForBrands/ui/LegendItem";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { impactMeaning } from "./athletePerformanceModel";
import { cx } from "./perfPieces";

// The Player Archetype info pop-up (Jayson's "Player Archetype Metrics"):
// how the label is picked (the payload's method), one row per metric (the
// code, what it asks, the athlete's value and rank), what the Impact rating
// beside the radar is (with its scale), then every archetype
// with the athlete's own one marked (" · You" on their own profile, their
// first name for anyone else). A dialog on desktop, the bottom sheet on
// phones (BmModal). `view` is athletePerformanceModel.archetypeView().

const PlayerArchetypeModal = ({ view, onClose }) => {
  const phone = usePhoneTheme();
  return (
    <BmModal title="Player Archetype Metrics" onClose={onClose} width={760}>
      <div className={phone ? "-mt-2 flex flex-col gap-4" : "flex flex-col gap-5 py-1"}>
        {view.method && <p className="bm-body-m text-bm-muted">{view.method}</p>}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
        <ul role="list" aria-label="Archetype metrics" className="flex flex-col gap-2">
          {view.metrics.map((m) => (
            <li
              key={m.key}
              className={
                phone
                  ? "grid grid-cols-[52px_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-[12px] border border-bm-border bg-bm-subtle px-4 py-[14px]"
                  : "grid grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-4 rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4"
              }
            >
              <span className="bm-label-l text-bm-orange">{m.code}</span>
              <span className="flex min-w-0 flex-col gap-1 break-words">
                <span className="bm-h-s text-bm-ink">{m.title}</span>
                {m.question && <span className="bm-body-m text-bm-muted">{m.question}</span>}
              </span>
              <span className={phone ? "col-start-2 flex items-baseline gap-2" : "flex flex-col items-end gap-1 text-right"}>
                <span className="bm-num-m tabular-nums text-bm-ink">{m.raw}</span>
                <span className="bm-label-m text-bm-muted">{m.top}</span>
              </span>
            </li>
          ))}
        </ul>
        {view.impact && (
          <p className="bm-body-m text-bm-muted">
            <strong className="font-semibold text-bm-ink">Impact rating.</strong> {impactMeaning(view.impact)}
          </p>
        )}
        {view.archetypes.length > 0 && (
          <>
            <h3 className="m-0 bm-h-s text-bm-ink">{view.archetypesTitle}</h3>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
            <ul role="list" aria-label={view.archetypesTitle} className={phone ? "flex flex-col gap-2" : "grid gap-2 sm:grid-cols-2"}>
              {view.archetypes.map((a) => (
                <li
                  key={a.name}
                  aria-current={a.own ? "true" : undefined}
                  className={cx(
                    "flex items-start gap-3 rounded-[8px] border px-4 py-3",
                    a.own ? "border-bm-orange bg-bm-orange/5" : "border-bm-border bg-white",
                  )}
                >
                  <ArchetypeDot color={a.color} size={10} className="mt-[5px]" />
                  <span className="flex min-w-0 flex-col gap-[2px] break-words">
                    <span className="bm-label-l text-bm-ink">{a.own ? `${a.name} · ${view.own}` : a.name}</span>
                    {a.description && <span className="bm-body-s text-bm-muted">{a.description}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </BmModal>
  );
};

export default PlayerArchetypeModal;
